import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "../../supabase/supabase";
import { ArrowLeft, Loader2, Save } from "lucide-react";

type Organization = {
  id: string;
  name: string;
  is_pro: boolean;
  feature_flags?: Record<string, boolean>;
};

const AVAILABLE_FEATURES = [
  {
    id: "schemaTemplates",
    name: "Schema Templates",
    description: "Enable JSON schema template creation and management",
  },
  {
    id: "featureTemplates",
    name: "Feature Templates",
    description: "Enable feature template creation and management",
  },
  {
    id: "promptengine",
    name: "Prompt Engine",
    description: "Enable prompt creation and management",
  },
  {
    id: "assistants",
    name: "Assistants",
    description: "Enable AI assistant creation and management",
  },
  {
    id: "calllogs",
    name: "Call Logs",
    description: "Enable call logs viewing and management",
  },
  {
    id: "leads",
    name: "Leads",
    description: "Enable leads management",
  },
  {
    id: "leaduploads",
    name: "Lead Uploads",
    description: "Enable lead upload functionality",
  },
  {
    id: "analytics",
    name: "Analytics Dashboard",
    description: "Enable advanced analytics and reporting features",
  },
  {
    id: "campaigns",
    name: "Campaigns",
    description: "Enable campaign creation and management",
  },
  {
    id: "slots",
    name: "Slots",
    description: "Enable slot scheduling and management",
  },
  {
    id: "phonenumbers",
    name: "Phone Numbers",
    description: "Enable phone number management",
  },
  {
    id: "callrequests",
    name: "Call Requests",
    description: "Enable call request management",
  },
  {
    id: "microprompts",
    name: "Micro Prompts",
    description: "Enable micro prompt creation and management",
  },
  // Call backends. New organizations start with only V2 voice on; the API server refuses a call
  // on a backend that is off.
  {
    id: "voice_v2",
    name: "V2 Voice Calls",
    description: "Phone calls and web sessions on the V2 agent server",
  },
  {
    id: "voice_v1",
    name: "V1 Voice Calls",
    description: "Phone calls and web audio on the V1 (LiveKit) agent",
  },
  {
    id: "video_v1",
    name: "V1 Video Interviews",
    description: "Video interview links and video sessions on the V1 (LiveKit) agent",
  },
];

const OrganizationFeaturesPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (id) {
      fetchOrganization(id);
    }
  }, [id]);

  const fetchOrganization = async (orgId: string) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("organizations")
        .select("*")
        .eq("id", orgId)
        .single();

      if (error) throw error;

      if (data) {
        setOrganization(data);
        // Initialize feature flags with defaults for any missing flags
        const flags = { ...data.feature_flags } || {};

        // Ensure all available features have a value (default to true if not specified)
        AVAILABLE_FEATURES.forEach((feature) => {
          if (flags[feature.id] === undefined) {
            flags[feature.id] = true;
          }
        });

        setFeatureFlags(flags);
      }
    } catch (error) {
      console.error("Error fetching organization:", error);
      toast({
        title: "Error",
        description: "Failed to fetch organization details",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFeatureToggle = (featureId: string, enabled: boolean) => {
    setFeatureFlags((prev) => ({
      ...prev,
      [featureId]: enabled,
    }));
  };

  const saveFeatureFlags = async () => {
    if (!organization) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("organizations")
        .update({ feature_flags: featureFlags })
        .eq("id", organization.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Feature flags updated successfully",
        variant: "default",
      });
    } catch (error) {
      console.error("Error updating feature flags:", error);
      toast({
        title: "Error",
        description: "Failed to update feature flags",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 px-4 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p>Loading organization details...</p>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Button variant="outline" onClick={() => navigate("/superadmin")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Super Admin
        </Button>
        <Card className="mt-6">
          <CardContent className="p-6 text-center">
            <p>Organization not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-6">
        <div>
          <Button variant="outline" onClick={() => navigate("/superadmin")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Super Admin
          </Button>
          <h1 className="text-3xl font-bold mt-4">{organization.name}</h1>
          <p className="text-muted-foreground">
            Manage feature flags for this organization
          </p>
        </div>
        <Button onClick={saveFeatureFlags} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Feature Flags</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {AVAILABLE_FEATURES.map((feature) => (
              <div key={feature.id}>
                <div className="flex items-center justify-between">
                  <div>
                    <Label
                      htmlFor={feature.id}
                      className="text-base font-medium"
                    >
                      {feature.name}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                  <Switch
                    id={feature.id}
                    checked={featureFlags[feature.id] || false}
                    onCheckedChange={(checked) =>
                      handleFeatureToggle(feature.id, checked)
                    }
                  />
                </div>
                <Separator className="mt-4" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>PRO Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="pro-status" className="text-base font-medium">
                PRO Features
              </Label>
              <p className="text-sm text-muted-foreground">
                Enable or disable PRO features for this organization
              </p>
            </div>
            <Switch
              id="pro-status"
              checked={organization.is_pro}
              onCheckedChange={async (checked) => {
                try {
                  const { error } = await supabase
                    .from("organizations")
                    .update({ is_pro: checked })
                    .eq("id", organization.id);

                  if (error) throw error;

                  setOrganization({
                    ...organization,
                    is_pro: checked,
                  });

                  toast({
                    title: "Success",
                    description: `PRO status ${checked ? "enabled" : "disabled"} successfully`,
                    variant: "default",
                  });
                } catch (error) {
                  console.error("Error updating PRO status:", error);
                  toast({
                    title: "Error",
                    description: "Failed to update PRO status",
                    variant: "destructive",
                  });
                }
              }}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrganizationFeaturesPage;
